import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Briefcase, Filter, Search, ShieldCheck, Star, X } from 'lucide-react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import {
  PRACTICE_AREAS,
  PRACTICE_AREA_LABELS,
  SPECIALTIES,
  SPECIALTY_LABELS,
  SPECIALTY_AREA,
  SENIORITY_LABELS,
  LANGUAGES,
  LANGUAGE_LABELS,
  type Language,
  type PracticeArea,
  type Seniority,
  type Specialty,
} from '@parecer/shared';
import { getInitials, formatBRL, cn } from '@/lib/utils';

interface LawyerCard {
  userId: string;
  name: string;
  bio?: string;
  oab: string;
  oabState: string;
  oabVerified?: boolean;
  specialties: Specialty[];
  tags?: string[];
  seniority?: Seniority;
  languages?: Language[];
  servesIndividuals?: boolean;
  servesCompanies?: boolean;
  averageRating?: number;
  totalReviews?: number;
  totalCompletedTasks?: number;
  startingPrice?: number;
}

export function Advogados() {
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState<PracticeArea | ''>('');
  const [specialtyFilter, setSpecialtyFilter] = useState<Specialty | ''>('');
  const [languageFilter, setLanguageFilter] = useState<Language | ''>('');
  const [audienceFilter, setAudienceFilter] = useState<'' | 'pf' | 'pj'>('');
  const [oabOnly, setOabOnly] = useState(false);
  const [minRating, setMinRating] = useState<number>(0);

  const { data, isLoading, error } = useQuery({
    queryKey: ['lawyers'],
    queryFn: () => api.get<{ items: LawyerCard[] }>('/lawyers?limit=50', { authenticated: false }),
  });

  // Filter client-side for now — small lawyer base. When it grows, push to
  // backend via lawyerListQuerySchema.
  const filtered = useMemo(() => {
    let arr = data?.items ?? [];
    const n = search
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
    if (n) {
      arr = arr.filter((l) => {
        const hay = [
          l.name,
          l.bio ?? '',
          l.specialties.map((s) => SPECIALTY_LABELS[s]).join(' '),
          (l.tags ?? []).join(' '),
        ]
          .join(' ')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '');
        return hay.includes(n);
      });
    }
    if (areaFilter) {
      arr = arr.filter((l) => l.specialties.some((s) => SPECIALTY_AREA[s] === areaFilter));
    }
    if (specialtyFilter) {
      arr = arr.filter((l) => l.specialties.includes(specialtyFilter));
    }
    if (languageFilter) {
      arr = arr.filter((l) => (l.languages ?? []).includes(languageFilter));
    }
    if (audienceFilter === 'pf') {
      arr = arr.filter((l) => l.servesIndividuals);
    } else if (audienceFilter === 'pj') {
      arr = arr.filter((l) => l.servesCompanies);
    }
    if (oabOnly) {
      arr = arr.filter((l) => l.oabVerified);
    }
    if (minRating > 0) {
      arr = arr.filter((l) => (l.averageRating ?? 0) >= minRating);
    }
    return arr;
  }, [data, search, areaFilter, specialtyFilter, languageFilter, audienceFilter, oabOnly, minRating]);

  // When area changes, narrow the specialty options to that area.
  const specialtyOptions = useMemo(() => {
    if (!areaFilter) return SPECIALTIES;
    return SPECIALTIES.filter((s) => SPECIALTY_AREA[s] === areaFilter);
  }, [areaFilter]);

  function resetFilters(): void {
    setSearch('');
    setAreaFilter('');
    setSpecialtyFilter('');
    setLanguageFilter('');
    setAudienceFilter('');
    setOabOnly(false);
    setMinRating(0);
  }

  const activeFilters =
    (areaFilter ? 1 : 0) +
    (specialtyFilter ? 1 : 0) +
    (languageFilter ? 1 : 0) +
    (audienceFilter ? 1 : 0) +
    (oabOnly ? 1 : 0) +
    (minRating > 0 ? 1 : 0);

  return (
    <MarketingLayout>
      <div className="container-px mx-auto max-w-7xl py-16">
        <h1 className="font-serif text-display-lg text-balance">Advogados parceiros</h1>
        <p className="mt-4 text-muted-foreground max-w-2xl">
          Profissionais com OAB ativa, especialidade definida e avaliações públicas. Cada um define
          sua tabela de preços.
        </p>

        {/* Filters */}
        <div className="mt-8 grid lg:grid-cols-[280px_1fr] gap-8">
          <aside className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar por nome, área, tag..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Grande área
              </label>
              <select
                value={areaFilter}
                onChange={(e) => {
                  setAreaFilter(e.target.value as PracticeArea | '');
                  setSpecialtyFilter('');
                }}
                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">Todas as áreas</option>
                {PRACTICE_AREAS.map((a) => (
                  <option key={a} value={a}>{PRACTICE_AREA_LABELS[a]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Especialidade
              </label>
              <select
                value={specialtyFilter}
                onChange={(e) => setSpecialtyFilter(e.target.value as Specialty | '')}
                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">Todas{areaFilter ? ` (${specialtyOptions.length})` : ''}</option>
                {specialtyOptions.map((s) => (
                  <option key={s} value={s}>{SPECIALTY_LABELS[s]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Idioma
              </label>
              <select
                value={languageFilter}
                onChange={(e) => setLanguageFilter(e.target.value as Language | '')}
                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">Qualquer</option>
                {LANGUAGES.map((l) => (
                  <option key={l} value={l}>{LANGUAGE_LABELS[l]}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Atende
              </label>
              <div className="mt-1.5 flex gap-2">
                {(['', 'pf', 'pj'] as const).map((v) => (
                  <button
                    key={v || 'all'}
                    type="button"
                    onClick={() => setAudienceFilter(v)}
                    className={cn(
                      'flex-1 rounded-md border px-3 py-2 text-xs transition-colors',
                      audienceFilter === v
                        ? 'border-accent bg-accent/15 text-foreground font-medium'
                        : 'border-border text-muted-foreground hover:border-accent/40',
                    )}
                  >
                    {v === '' ? 'Todos' : v === 'pf' ? 'PF' : 'PJ'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Avaliação mínima
              </label>
              <div className="mt-1.5 flex gap-2">
                {[0, 3, 4, 4.5].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setMinRating(v)}
                    className={cn(
                      'flex-1 inline-flex items-center justify-center gap-1 rounded-md border px-2 py-2 text-xs transition-colors',
                      minRating === v
                        ? 'border-accent bg-accent/15 text-foreground font-medium'
                        : 'border-border text-muted-foreground hover:border-accent/40',
                    )}
                  >
                    {v === 0 ? 'Qualquer' : `${v}+`}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 accent-accent"
                checked={oabOnly}
                onChange={(e) => setOabOnly(e.target.checked)}
              />
              Apenas OAB verificada
            </label>

            {activeFilters > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={resetFilters}
                className="w-full gap-1"
              >
                <X className="h-3.5 w-3.5" />
                Limpar {activeFilters} filtro{activeFilters > 1 ? 's' : ''}
              </Button>
            )}
          </aside>

          <div>
            <div className="mb-4 flex items-center justify-between text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <Filter className="h-4 w-4" />
                {isLoading ? 'Carregando…' : `${filtered.length} resultado${filtered.length === 1 ? '' : 's'}`}
              </span>
            </div>

            {error && (
              <Card>
                <CardContent className="pt-6 text-muted-foreground text-sm">
                  Não foi possível carregar a lista. Tente novamente em instantes.
                </CardContent>
              </Card>
            )}
            {data && filtered.length === 0 && !error && (
              <Card>
                <CardContent className="pt-6 text-muted-foreground">
                  Nenhum advogado bate com os filtros atuais.{' '}
                  {activeFilters > 0 && (
                    <button onClick={resetFilters} className="text-accent hover:underline">
                      Limpar filtros
                    </button>
                  )}
                </CardContent>
              </Card>
            )}
            {filtered.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {filtered.map((l) => (
                  <Link key={l.userId} to={`/advogados/${l.userId}`} className="focus-ring">
                    <Card className="h-full hover:border-accent/40 transition-colors">
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                          <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center font-medium">
                            {getInitials(l.name)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h3 className="font-serif text-lg truncate">{l.name}</h3>
                              {l.oabVerified && (
                                <ShieldCheck className="h-3.5 w-3.5 text-success flex-none" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              OAB/{l.oabState} {l.oab}
                              {l.seniority && <> · {SENIORITY_LABELS[l.seniority]}</>}
                            </p>
                          </div>
                          {l.averageRating !== undefined && (
                            <div className="flex items-center gap-1 text-sm">
                              <Star className="h-3.5 w-3.5 fill-accent text-accent" />
                              {l.averageRating.toFixed(1)}
                            </div>
                          )}
                        </div>
                        {l.bio && (
                          <p className="mt-4 text-sm text-muted-foreground line-clamp-3">{l.bio}</p>
                        )}
                        <div className="mt-4 flex flex-wrap gap-1">
                          {l.specialties?.slice(0, 3).map((s) => (
                            <Badge key={s} variant="muted" className="text-[10px]">
                              {SPECIALTY_LABELS[s]}
                            </Badge>
                          ))}
                          {l.specialties && l.specialties.length > 3 && (
                            <Badge variant="muted" className="text-[10px]">
                              +{l.specialties.length - 3}
                            </Badge>
                          )}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                          {(l.servesIndividuals || l.servesCompanies) && (
                            <span>
                              {[l.servesIndividuals && 'PF', l.servesCompanies && 'PJ']
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          )}
                          {(l.languages?.length ?? 0) > 0 && (
                            <span>
                              {l.languages!.map((lng) => LANGUAGE_LABELS[lng]).join(' · ')}
                            </span>
                          )}
                        </div>
                        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <Briefcase className="h-3 w-3" />
                            {l.totalCompletedTasks ?? 0} entregas
                          </span>
                          {l.startingPrice !== undefined && (
                            <span>A partir de {formatBRL(l.startingPrice)}</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
}
